import { IAttribute, IProductWithAttributes } from '@gn-audio/core/api/product-catalogue/models';
import { copy } from 'angular';
import { Injectable } from 'angular-ts-decorators';

import { IFilter, IOption, OptionBase } from '../models';

/**
 * @deprecated Should be converted into util functions!
 */
export interface ICustomizerFilterService {
  /**
   * Checks whether specified group fits collection of specified filters.
   * @param group {IProductWithAttributes} A group to check fitness.
   * @param filters {IFilter[]} A collection of filters the check group by. Only filters with values are used.
   * @returns {boolean} A boolean value indicating whether group fits the filters.
   */
  match(group: IProductWithAttributes, filters: IFilter[]): boolean;

  /**
   * Filters groups using specified collection of filters.
   * @param groups {IProductWithAttributes[]} A collection of groups needs to be filtered.
   * @param filters {IFilter[]} A collection of filters to filter groups by. Only filters with values are used.
   * @returns {IProductWithAttributesItem[]} A collection of passed groups.
   */
  filter(groups: IProductWithAttributes[], filters: IFilter[]): IProductWithAttributes[];

  /**
   * Prepares filters (visibility) using attributes of specified groups.
   * @param groups {IProductWithAttributes[]} A collection of groups to prepare by.
   * @param filters {IFilter[]} A collection of fitlers that needs to prepared.
   */
  prepare(groups: IProductWithAttributes[], filters: IFilter[]): void;

  /**
   * Processes filters (disable state) using attributes of specified groups.
   * @param groups {IProductWithAttributes[]} A collection of groups to process by.
   * @param filters {IFilter[]} A collection of fitlers that needs to processed.
   */
  process(groups: IProductWithAttributes[], filters: IFilter[]): void;

  /**
   * Forecasts disable state of options of specifeid array of filters.
   * @param groups {IProductWithAttributes[]} An array of groups to forecast by.
   * @param filters {IFilter[]} An array of filters to make a forecast.
   * @returns {IFilter[]} An array of cloned filters with updated options' disable state.
   */
  forecast(groups: IProductWithAttributes[], filters: IFilter[]): IFilter[];
}

@Injectable('customizerFilterService')
export class CustomizerFilterService implements ICustomizerFilterService {
  public static $inject: ReadonlyArray<string> = [];

  public match(group: IProductWithAttributes, filters: IFilter[] = []): boolean {
    const product: IProductWithAttributes = group;

    return filters
      .filter((f: IFilter): boolean => f.filterable)
      .filter((f: IFilter): boolean => f.hasValue())
      .every(
        (f: IFilter): boolean =>
          product.attributes.some(
            (a: IAttribute): boolean => {
              return a.key === f.key && a.valueKey === f.value;
            },
          ),
      );
  }

  public filter(groups: IProductWithAttributes[] = [], filters: IFilter[] = []): IProductWithAttributes[] {
    const matched: IProductWithAttributes[] = [];

    for (const group of groups) {
      if (this.match(group, filters)) {
        matched.push(group);
      }
    }

    return matched;
  }

  public prepare(groups: IProductWithAttributes[] = [], filters: IFilter[] = []): void {
    let filterKey: string;
    let attributes: IAttribute[];
    let options: IOption[];

    for (const filter of filters) {
      if (!filter.filterable) {
        continue;
      }

      filterKey = filter.key;
      for (const group of groups) {
        attributes = group.attributes.filter((a: IAttribute) => a.key === filterKey);
        if (attributes.length === 0) {
          continue;
        }

        options = filter.options.filter((o: OptionBase) => attributes.some((a: IAttribute) => a.valueKey === o.value));
        options.forEach(
          (o: IOption): void => {
            o.visible = true;
          },
        );
      }
    }
  }

  public process(groups: IProductWithAttributes[] = [], filters: IFilter[] = []): void {
    const forecastedFilters: IFilter[] = this.forecast(groups, filters);

    filters.forEach((f: IFilter, i: number) => {
      f.options.forEach((o: IOption, j: number) => {
        o.disabled = forecastedFilters[i].options[j].disabled;
      });
    });
  }

  public forecast(groups: IProductWithAttributes[] = [], filters: IFilter[] = []): IFilter[] {
    const forecastedFilters: IFilter[] = [];
    let forecastedGroups: IProductWithAttributes[];

    copy(filters, forecastedFilters);
    for (const forecastedFilter of forecastedFilters) {
      if (!forecastedFilter.filterable) {
        continue;
      }

      if (!Array.isArray(forecastedFilter.options) || !forecastedFilter.options.some((o: IOption) => o.visible)) {
        continue;
      }

      const originalValue: string = forecastedFilter.value;
      for (const forecastedFilterOption of forecastedFilter.options) {
        forecastedFilter.value = forecastedFilterOption.value;
        forecastedGroups = this.filter(groups, forecastedFilters);
        forecastedFilterOption.disabled = forecastedGroups.length === 0;
      }

      forecastedFilter.value = originalValue;
    }

    return forecastedFilters;
  }
}
